import { Box, Card, CardContent, CardHeader, Hidden, Skeleton, Stack } from '@mui/material';
import { useMediaQuery, Theme } from '@mui/material';

export function PatientWaitingRoomSkeleton() {
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md') && theme.breakpoints.up('sm'));
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 'unset !important',
        backgroundColor: 'var(--app-surface)',
        boxShadow: 'rgba(0, 0, 0, 0.05) 0px 2px 4px !important',
        border: 'none',
      }}
    >
      {/* Header bar — real brand color with a faint text shimmer inside */}
      <CardHeader
        title={
          <Skeleton
            variant='text'
            width={140}
            height={22}
            sx={{ backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: 1 }}
          />
        }
        sx={{
          backgroundColor: 'var(--app-brand-primary)',
          padding: '7px 20px',
          minHeight: 46,
        }}
      />

      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: isMobile ? '20px 20px 15px' : isTablet ? '20px' : '30px',
          gap: '20px',
        }}
      >
        {/* Mobile: self-preview above the card (stub rectangle) */}
        <Hidden smUp>
          <Skeleton variant='rounded' width='100%' height={140} sx={{ borderRadius: 2 }} />
        </Hidden>

        {/* Welcome message text lines */}
        <Box>
          <Skeleton variant='text' width='78%' height={20} />
          <Skeleton variant='text' width='55%' height={20} sx={{ mt: 0.5 }} />
        </Box>

        {/* ── Desktop / Tablet main row ── */}
        <Hidden smDown>
          <Stack direction='row' spacing='26px' alignItems='flex-start' justifyContent='space-between'>
            {/* Left: avatar + button + details */}
            <Stack direction='row' spacing='26px' alignItems='flex-start' sx={{ flex: 1, minWidth: 0 }}>
              {/* Avatar column */}
              <Stack spacing='10px' alignItems='stretch' sx={{ flexShrink: 0 }}>
                <Skeleton variant='rounded' width={150} height={150} sx={{ borderRadius: 1.5 }} />
                <Skeleton variant='rounded' width={150} height={44} sx={{ borderRadius: 1 }} />
              </Stack>

              {/* Details column */}
              <Stack spacing='8px' alignItems='flex-start' sx={{ pt: '4px', flex: 1 }}>
                {/* Provider name */}
                <Skeleton variant='text' width={210} height={32} />
                {/* Specialty */}
                <Skeleton variant='text' width={150} height={22} sx={{ mt: -0.5 }} />

                {/* Status row */}
                <Stack direction='row' spacing='10px' alignItems='center' sx={{ mt: '4px' }}>
                  <Skeleton variant='circular' width={18} height={18} sx={{ flexShrink: 0 }} />
                  <Skeleton variant='text' width={110} height={20} />
                </Stack>

                {/* Organization row */}
                <Stack direction='row' spacing='10px' alignItems='center'>
                  <Skeleton variant='rounded' width={18} height={20} sx={{ flexShrink: 0, borderRadius: 0.5 }} />
                  <Skeleton variant='text' width={170} height={20} />
                </Stack>

                {/* Email row */}
                <Stack direction='row' spacing='10px' alignItems='center'>
                  <Skeleton variant='rounded' width={22} height={18} sx={{ flexShrink: 0, borderRadius: 0.5 }} />
                  <Skeleton variant='text' width={190} height={20} />
                </Stack>
              </Stack>
            </Stack>

            {/* Tablet-only video preview */}
            {isTablet && (
              <Skeleton variant='rounded' width={210} height={165} sx={{ flexShrink: 0, borderRadius: 2 }} />
            )}
          </Stack>
        </Hidden>

        {/* ── Mobile main row ── */}
        <Hidden smUp>
          <Stack direction='column' spacing='15px'>
            <Stack direction='row' spacing='15px'>
              {/* Small avatar */}
              <Skeleton variant='rounded' width={100} height={100} sx={{ flexShrink: 0, borderRadius: 1.5 }} />

              <Stack spacing='8px' justifyContent='flex-end' sx={{ flex: 1 }}>
                {/* Status */}
                <Stack direction='row' spacing='4px' alignItems='center'>
                  <Skeleton variant='circular' width={16} height={16} sx={{ flexShrink: 0 }} />
                  <Skeleton variant='text' width={80} height={20} />
                </Stack>
                {/* Message button */}
                <Skeleton variant='rounded' width='100%' height={40} sx={{ borderRadius: 1 }} />
              </Stack>
            </Stack>

            {/* Provider name */}
            <Skeleton variant='text' width={180} height={28} />

            {/* Org row */}
            <Stack direction='row' spacing='10px' alignItems='center'>
              <Skeleton variant='rounded' width={18} height={20} sx={{ flexShrink: 0, borderRadius: 0.5 }} />
              <Skeleton variant='text' width={150} height={20} />
            </Stack>
          </Stack>
        </Hidden>

        {/* Bio text */}
        <Box sx={{ mt: '4px' }}>
          <Skeleton variant='text' width='100%' height={20} />
          <Skeleton variant='text' width='92%' height={20} sx={{ mt: 0.5 }} />
          <Skeleton variant='text' width='68%' height={20} sx={{ mt: 0.5 }} />
        </Box>
      </CardContent>
    </Card>
  );
}
